import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons"
import { Modal } from "antd"
import React, { useEffect } from "react"

import type { PortalPhoto } from "../types/portal.types.ts"

interface PhotoViewerProps {
  photos: PortalPhoto[]
  index: number
  onClose: () => void
  onNavigate: (index: number) => void
}

export function PhotoViewer({ photos, index, onClose, onNavigate }: PhotoViewerProps) {
  const photo = photos[index]
  const hasPrev = index > 0
  const hasNext = index < photos.length - 1

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && hasPrev) onNavigate(index - 1)
      if (e.key === "ArrowRight" && hasNext) onNavigate(index + 1)
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKey)
    return () => window.removeEventListener("keydown", handleKey)
  }, [index, hasPrev, hasNext, onNavigate, onClose])

  if (!photo) return null

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      closable={false}
      // `100%` of the modal's own fixed, viewport-inset wrapper — not
      // `100vw`/`100vh`, which stay pinned to the full viewport including
      // whatever a scrollbar or mobile browser chrome is currently covering,
      // and can overflow or clip against the actually-visible area.
      width="100%"
      style={{ top: 0, maxWidth: "100%", padding: 0 }}
      styles={{
        body: { height: "100dvh", padding: 0 },
        content: { padding: 0, borderRadius: 0, height: "100dvh" }
      }}
    >
      <div className="relative flex h-full w-full items-center justify-center bg-black">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white"
        >
          <CloseOutlined />
        </button>

        {hasPrev && (
          <button
            type="button"
            onClick={() => onNavigate(index - 1)}
            aria-label="Previous photo"
            className="absolute left-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white md:left-4"
          >
            <LeftOutlined />
          </button>
        )}
        {hasNext && (
          <button
            type="button"
            onClick={() => onNavigate(index + 1)}
            aria-label="Next photo"
            className="absolute right-2 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white md:right-4"
          >
            <RightOutlined />
          </button>
        )}

        <img
          src={photo.file}
          alt={photo.original_filename}
          className="max-h-full max-w-full object-contain"
        />

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/40 px-3 py-1 text-xs text-white">
          {index + 1} / {photos.length}
        </div>
      </div>
    </Modal>
  )
}
